import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { Alert, Box, Button, Divider, MenuItem, useTheme } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { DUPLICATED_EMAIL_ERROR, DUPLICATED_PHONE_ERROR, DUPLICATED_WHATSAPP_ERROR, MISSING_FIELDS_ERROR } from '@bandido/shared/constants';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { DocumentType } from '@frontend/features/auth/types';
import { countries } from '@frontend/features/common/constants/countries';
import { CountryFlag } from '@frontend/features/common/components/CountryFlag';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetAllAgencies } from '@frontend/features/agencies/queries';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { Can } from '@casl/react';

interface FormProps {
  onClose: () => void;
}

interface CreateClientDTO {
  firstName: string;
  lastName: string;
  agency?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  email?: string;
  address1?: string;
  address2?: string;
  country?: string;
  city?: string;
  postalCode?: string;
  documentType?: string;
  documentNumber?: string;
  whatsappNumber?: string;
}

export const Form = ({ onClose }: FormProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const { data: agencies } = useGetAllAgencies({
    pageSize: 10000,
    currentPage: 1,
    search: '',
  });

  const { ability } = useAuth();

  const resolver = yupResolver(
    yup.object().shape({
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      agency: ability && ability.can('view', 'AgencySelector') ? yup.string().required() : yup.string().optional(),
      email: yup.string().email().optional(),
      whatsappNumber: yup.string().optional(),
      phoneCountryCode: yup
        .string()
        .optional(),
      phoneNumber: yup.string().optional(),
      address1: yup.string().optional(),
      address2: yup.string().optional(),
      country: yup
        .string()
        .optional(),
      city: yup.string().optional(),
      postalCode: yup.string().optional(),
      documentType: yup.mixed<DocumentType>().oneOf(Object.values(DocumentType)).optional(),
      documentNumber: yup.string().optional(),
      form: yup.string().optional().test(
        'email-or-phone',
        function() {
          const { email, phoneNumber, phoneCountryCode, whatsappNumber } = this.parent;
          const hasEmail = Boolean(email);
          const hasWhatsapp = Boolean(whatsappNumber);
          const hasPhone = Boolean(phoneCountryCode) && Boolean(phoneNumber);
  
          if (hasEmail || hasPhone || hasWhatsapp) {
            return true;
          } else {
            return this.createError({
              path: 'form',
              message: t('clients.create.createdErrorMissingFields')
            });
          }
        }
      )
    })
  );

  const methods = useForm({
    resolver,
  });

  const { handleSubmit, formState: { errors } } = methods;

  const onSubmit = async (data: CreateClientDTO) => {
    setLoading(true);
    try {
      await client.post('/client', data);
      addToast({
        message: t('clients.create.createdSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries();
      onClose();
    } catch (e: any) {
      const errorResponse = e.response.data.message.error;

      if (errorResponse === DUPLICATED_EMAIL_ERROR) {
        addToast({
          message: t('clients.create.createdErrorEmail'),
          color: 'error',
        });

      } else if (errorResponse === DUPLICATED_PHONE_ERROR) {
        addToast({
          message: t('clients.create.createdErrorPhone'),
          color: 'error',
        });
      } else if (errorResponse === MISSING_FIELDS_ERROR) {
        addToast({
          message: t('clients.create.createdErrorMissingFields'),
          color: 'error',
        });
        } else if (errorResponse === DUPLICATED_WHATSAPP_ERROR) {
        addToast({
          message: t('clients.create.createdErrorWhatsapp'),
          color: 'error',
        });
      } else {
        addToast({
          message: t('clients.create.createdError'),
          color: 'error',
        });
      }
    } finally {
      setLoading(false);
    }

  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'auto',
            paddingTop: theme.spacing(2),
          }}
        >
          {errors?.form?.message && (
            <Box gridColumn="span 12" gap={2}>
              <Alert severity="error">
                {errors.form.message}
              </Alert>
            </Box>
          )}

          <Can I="view" a="AgencySelector" ability={ability}>
            <Box gridColumn="span 12">
              <ControlledSelect
                variant="outlined"
                name="agency"
                control={methods.control}
                placeholder={t('common.form.select')}
                label={t('clients.create.agency')}
                size="small"
                required
                errorMessage={t('common.form.required')}
                fullWidth
              >
                {agencies?.items.map((agency) => (
                  <MenuItem key={agency.id} value={agency.id}>
                    {agency.name}
                  </MenuItem>
                ))}
              </ControlledSelect>
            </Box>
          </Can>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="firstName"
              label={t('clients.create.firstName')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="lastName"
              label={t('clients.create.lastName')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="email"
              label={t('clients.create.email')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledSelect
              variant="outlined"
              name="country"
              control={methods.control}
              placeholder={t('common.form.select')}
              label={t('clients.create.country')}
              size="small"
              fullWidth
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.name}>
                  <CountryFlag countryCode={country.code} />
                  {country.name}
                </MenuItem>
              ))}
            </ControlledSelect>
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="city"
              label={t('clients.create.city')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="postalCode"
              label={t('clients.create.postalCode')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>

          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="address1"
              label={t('clients.create.address1')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="address2"
              label={t('clients.create.address2')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 5">
            <ControlledSelect
              variant="outlined"
              name="phoneCountryCode"
              control={methods.control}
              placeholder={t('common.form.select')}
              label={t('clients.create.phoneCountryCode')}
              size="small"
              fullWidth
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.phoneCode}>
                  <CountryFlag countryCode={country.code} />
                  {country.code} (+{country.phoneCode})
                </MenuItem>
              ))}
            </ControlledSelect>
          </Box>
          <Box gridColumn="span 7">
            <ControlledTextField
              control={methods.control}
              name="phoneNumber"
              label={t('clients.create.phoneNumber')}
              placeholder={t('common.form.onlyNumbers')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="whatsappNumber"
              label={t('clients.create.whatsappNumber')}
              placeholder={t('common.form.onlyNumbers')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 4">
            <ControlledSelect
              variant="outlined"
              name="documentType"
              control={methods.control}
              placeholder={t('common.form.select')}
              label={t('clients.create.documentType')}
              size="small"
              fullWidth
              defaultValue={DocumentType.CI}
            >
              <MenuItem value={DocumentType.CI}>{DocumentType.CI}</MenuItem>
              <MenuItem value={DocumentType.DNI}>{DocumentType.DNI}</MenuItem>
              <MenuItem value={DocumentType.PASSPORT}>{DocumentType.PASSPORT}</MenuItem>
            </ControlledSelect>
          </Box>
          <Box gridColumn="span 8">
            <ControlledTextField
              control={methods.control}
              name="documentNumber"
              label={t('clients.create.documentNumber')}
              placeholder={t('common.form.onlyNumbers')}
              fullWidth
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <LoadingButton loading={loading} disabled={loading} variant="contained" color="primary" type="submit" size="medium">
              {t('common.form.save')}
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
